import { Col, Form, Input, Modal, Row, Select, Upload, } from "antd";
import { useForm } from "antd/lib/form/Form";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DraggableModal } from "../../../../components/DraggableModal";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import { MapAddSingleMarker } from "../../../../components/maps/MapAddPoint";
import { GeoJsonPoint } from "../../../../models/geoJsonPoint";
import { Geometry } from "../../../../models/geoJson";
import SelectOwnerShipTypes from "../../../../components/selects/SelectOwnerShipTypes";
import { UploadOutlined } from "@ant-design/icons";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import { useLazyGetRegisterStatusListQuery } from "../../../../redux/api/handBooksApi/organic/registerStatusApi";
import { EntLogisticsCenter, EntLogisticsCenterCapacity } from "../../../../models/organic/logisticsCenter";
import { LogisticsCenterItems } from "./LogisticsCenterItems";

enum FormField {
  centerName,
  description,
  regionId,
  areaId,
  territorialUnitId,
  latitude,
  longitude,
  owner,
  ownershipTypeId,
  registerStatusId,
  commissioningYear,
  files,
}

interface IProps {
  show: boolean;
  item: EntLogisticsCenter;
  onHide: () => void;
  onSubmit: (pos: EntLogisticsCenter) => Promise<boolean>;
}

export const AddStorageModal = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [deleteFileIds, setDeleteFileIds] = useState<number[]>([]);
  const [items, setItems] = useState<EntLogisticsCenterCapacity[]>([]);

  const [geoJsonData, setGeoJsonData] = useState<any>(null);
  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) {
	  setFieldsFromEntity();
	  if (props.item.id === null) {
		setItems([]);
	  } else if (props.item.capacity && props.item.capacity.length > 0) {
		setItems(props.item.capacity.map((item) => ({
		  ...item,
		  storageAreaM2: item.storageAreaM2 || item.storageAreaM2,
		  dailyThroughputTon: item.dailyThroughputTon || item.dailyThroughputTon,
		  coldStorageVolumeM3: item.coldStorageVolumeM3 || item.coldStorageVolumeM3,
		  transportAccessIds: item.transportAccessIds || (item.transportAccess?.id ? [item.transportAccess.id] : null),
		  certificationsIds: item.certificationsIds || (item.certifications?.id ? [item.certifications.id] : null),
		  availableServicesIds: item.availableServicesIds || (item.availableServices?.id ? [item.availableServices.id] : null),

		})));
	  } else {
		setItems([]);
	  }
	  if (props.item.files) {
		const filesForUpload = props.item.files.map((f) => ({
		  uid: f.id,
		  name: f.name,
		  status: "done",
		  url: f.path,
		}));
		setFileList(filesForUpload);
	  } else {
		setFileList([]);
	  }
	}
	setSelectedRegionId(props.item.region?.id || null);
	setSelectedAreaId(props.item.area?.id || null);
	setSelectedAreaCode(props.item.territorialUnit?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.centerName, value: props.item.centerName },
	  { name: FormField.description, value: props.item.description },
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.owner, value: props.item.owner },
	  { name: FormField.ownershipTypeId, value: props.item.ownershipType?.id },
	  { name: FormField.registerStatusId, value: props.item.registerStatus?.id },
	  { name: FormField.commissioningYear, value: props.item.commissioningYear },
	]);
	if (props.item.capacity && props.item.capacity.length > 0) {
	  setItems(props.item.capacity.map((item) => ({
		...item,
		storageAreaM2: item.storageAreaM2 || item.storageAreaM2,
		dailyThroughputTon: item.dailyThroughputTon || item.dailyThroughputTon,
		coldStorageVolumeM3: item.coldStorageVolumeM3 || item.coldStorageVolumeM3,
		transportAccessIds: item.transportAccessIds || (item.transportAccess?.id ? [item.transportAccess.id] : null),
		certificationsIds: item.certificationsIds || (item.certifications?.id ? [item.certifications.id] : null),
		availableServicesIds: item.availableServicesIds || (item.availableServices?.id ? [item.availableServices.id] : null),
	  })));
	}
  };

  const updateGeoJsonPoint = () => {
	const latitude = props.item.latitude;
	const longitude = props.item.longitude;

	if (latitude !== undefined && longitude !== undefined) {
	  const geoJsonPoint = new GeoJsonPoint();
	  geoJsonPoint.geometry = new Geometry();
	  geoJsonPoint.geometry.coordinates = [longitude, latitude];
	  setGeoJsonData(geoJsonPoint);
	} else {
	  console.error(t('breeding_farms.error_geometry'));
	}
  };

  useEffect(() => {
	if (props.item.latitude && props.item.longitude) {
	  updateGeoJsonPoint();
	} else {
	  setGeoJsonData(null);
	}
  }, [props.item.latitude, props.item.longitude]);

  const setEntityFromFields = (): EntLogisticsCenter => {
	let res = _.cloneDeep(props.item);
	res.centerName = form.getFieldValue(FormField.centerName);
	res.description = form.getFieldValue(FormField.description);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.owner = form.getFieldValue(FormField.owner);
	res.ownershipTypeId = form.getFieldValue(FormField.ownershipTypeId);
	res.registerStatusId = form.getFieldValue(FormField.registerStatusId);
	res.commissioningYear = form.getFieldValue(FormField.commissioningYear);
	if (geoJsonData !== null) {
	  res.latitude = geoJsonData.geometry?.coordinates[1] as number;
	  res.longitude = geoJsonData.geometry?.coordinates[0] as number;
	}
	res.files = fileList.map((file) => file.originFileObj || file);
	res.deletedFilesIds = deleteFileIds;
	res.capacity = items;

	return res;
  };

  const hideModal = () => {
	props.onHide();
	setSelectedRegionId(null);
	setSelectedRegionPolygonJson(null);
	setSelectedAreaCode(null);
	setGeoJsonData(null);
	setFileList([]);
	setDeleteFileIds([]);
  };

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  if (geoJsonData !== null) {
		const data = setEntityFromFields();
		const result = await props.onSubmit(data);
		if (result) hideModal();
	  } else {
		Modal.error({
		  title: t("msg.error"),
		  content: t("breeding_farms.choose_location"),
		});
	  }
	} finally {
	  setLoading(false);
	}
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<DraggableModal
	  title={ props.item.id === null ? t("general.add") : t("general.edit") }
	  visible={ props.show }
	  onCancel={ hideModal }
	  onSubmit={ handleSubmit }
	  submitLoading={ loading }
	  width={ 800 }
	>
	  <Form
		autoComplete="off"
		layout="vertical"
		form={ form }
	  >
		<Row style={ { margin: 0 } } gutter={ 16 }>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label="Название центра"
			  name={ FormField.centerName }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите название"/>
			</Form.Item>
			<Form.Item
			  label="Владелец"
			  name={ FormField.owner }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Input placeholder="Введите юридическое лицо"/>
			</Form.Item>
			<Form.Item
			  label="Год ввода в эксплуатацию"
			  name={ FormField.commissioningYear }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <Select placeholder="Выберите год" allowClear>
				{ years.map((year) => (
				  <Select.Option key={ year } value={ year }>
					{ year }
				  </Select.Option>
				)) }
			  </Select>
			</Form.Item>
			<Form.Item
			  label="Тип собственности"
			  name={ FormField.ownershipTypeId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectOwnerShipTypes/>
			</Form.Item>
		  </Col>
		  <Col className="gutter-row" span={ 12 }>
			<Form.Item
			  label="Статус регистрации"
			  name={ FormField.registerStatusId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>

			  <GeneralSelect<EntBasicHB, EntInitialFilter>
				queryHook={ useLazyGetRegisterStatusListQuery }
				initialFilter={ new EntInitialFilter() }
			  />
			</Form.Item>
			<Form.Item
			  label={ t("titles.territorial_unit_area") }
			  name={ FormField.regionId }
			  rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			>
			  <SelectTerritorialUnitsCode
				parentId={ 1 }
				onChange={ (value, option: any) => {
				  setSelectedRegionId(Number(value));
				  setSelectedRegionPolygonJson(option?.data?.polygonJson);
				  setSelectedRegionCode(option?.data?.code);
				  setSelectedAreaCode(null);
				  setSelectedAreaId(null);
				  form.setFieldsValue({
					[FormField.regionId]: value,
					[FormField.areaId]: null,
					[FormField.territorialUnitId]: null,
				  });
				} }
			  />
			</Form.Item>
			{ selectedRegionId || props.item.regionId ? (
			  <Form.Item
				label={ t("titles.territorialUnitDistricts") }
				name={ FormField.areaId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode
				  type={ ["AREA", "CITY_OF_REPUBLICAN_SIGNIFICANCE"] }
				  parentId={ selectedRegionId }
				  onChange={ (value, option: any) => {
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedAreaCode(option?.data?.code || null);
					setSelectedAreaId(value as number);
					form.setFields([
					  { name: FormField.territorialUnitId, value: null },
					]);
				  } }
				/>
			  </Form.Item>
			) : null }
			{ selectedAreaCode || props.item.territorialUnitId ? (
			  <Form.Item
				label={ t("titles.territorialUnitVillage") }
				name={ FormField.territorialUnitId }
				rules={ [
				  { required: true, message: t("msg.fill_in_the_field") },
				] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			) : null }
		  </Col>
		</Row>
		<Col span={ 24 }>
		  <Form.Item label="Примечание" name={ FormField.description }>
			<Input.TextArea/>
		  </Form.Item>
		</Col>
		<LogisticsCenterItems items={ items } setItems={ setItems }/>
		<Row style={ { marginTop: 16, display: "flex" } }>
		  <Col span={ 24 }>
			<Form.Item label="Файлы" name={ FormField.files }>
			  <Upload
				fileList={ fileList }
				onChange={ ({ fileList }) => setFileList(fileList) }
				beforeUpload={ () => false }
				multiple
				accept="image/*,.pdf,.doc,.docx"
				listType="picture-card"
				onRemove={ (file) => {
				  if (file.uid && !file.originFileObj) {
					setDeleteFileIds((prev) => [...prev, Number(file.uid)]);
				  }
				} }
			  >
				{ fileList.length >= 8 ? null : (
				  <div>
					<UploadOutlined/>
					<div style={ { marginTop: 8 } }>Загрузить</div>
				  </div>
				) }
			  </Upload>
			</Form.Item>
		  </Col>
		</Row>
		<MapAddSingleMarker
		  polygonJson={ selectedRegionPolygonJson }
		  setGeoJsonData={ setGeoJsonData }
		  geoJsonData={ geoJsonData }
		/>
	  </Form>
	</DraggableModal>
  );
};
